import { ReactNode, useEffect, useState } from "react";
import CheckboxItem from "../CheckboxItem/CheckboxItem";
import { cn } from "@/lib/utils";
import { FormLabel, FormMessage } from "@/components/ui/form";

type CheckboxOption = {
  label: string | ReactNode;
  id: string;
};

type CheckboxGroupPropsType = {
  options: CheckboxOption[];
  setValue?: (value: string | string[]) => void;
  value?: string[];
  column?: boolean;
  label?: string;
  error?: string;
} & (
  | {
      isSingle: true;
      // defaultValue?: string;
    }
  | {
      isSingle?: false;
      // defaultValue?: string[];
    }
);

/**
 * A component that renders a group of checkboxes that can optionally behave as radio buttons.
 * @param {CheckboxOption[]} props.options - Array of options that the checkbox group will display.
 * @param {boolean} [props.isSingle=false] - Determines whether the checkbox group behaves as a single-select (radio buttons).
 * @param {string|string[]} [props.value] - The default selected value(s). This should be an array of strings.
 * @param {string} [props.label] - Label of checkbox group.
 * @param {boolean} [props.column] - Determines whether the checkbox group is oriented vertical.
 * @returns {JSX.Element} The rendered checkbox group.
 */
function CheckboxGroup({ options, isSingle = false, value, setValue, column, label, error }: CheckboxGroupPropsType) {
  const [checked, setChecked] = useState<string | string[]>(value || (isSingle === true ? "" : [""]));
  useEffect(() => {
    if (typeof setValue !== "undefined") setValue(checked);
  }, [checked]);
  useEffect(() => {
    if (typeof setValue !== "undefined") setValue(checked);
  }, []);
  const handleCheckboxChange = (id: string) => (isChecked: boolean) => {
    if (isSingle) {
      isChecked && setChecked([id]);
      return;
    } else {
      isChecked
        ? setChecked((current) => [...current, id])
        : setChecked((current) => (current as string[]).filter((x) => x !== id));
      return;
    }
  };
  return (
    <div className="space-y-2">
      <FormLabel>{label}</FormLabel>
      <div className={cn("flex gap-2", column && "flex-col")}>
        {options.map(({ label, id }) => (
          <CheckboxItem
            key={id}
            label={label}
            id={id}
            onChange={handleCheckboxChange(id)}
            checked={!!checked?.includes(id)}
          />
        ))}
      </div>
      {!!error && <FormMessage>{error}</FormMessage>}
    </div>
  );
}

export default CheckboxGroup;
